import { useEventBus } from "$root/event-bus";
import type { EventInstrumentPropagationUpdate } from "@eu.mdotm.api/sphere";

/**
 * Subscribes to the three propagation events (schedule-changed, started, done).
 *
 * One handler for all three on purpose: every event carries the *full* propagation state, so there is
 * nothing to derive per event type and the caller applies each payload through the same revision guard.
 * Callers that need to react to a completion specifically (e.g. the global failure toast) can read
 * `lastRun` off the state.
 *
 * Plain `useEventBus`, **not** `useGroupEventBus`: the payload is idempotent and last-write-wins
 * on revision, so batching buys nothing — and `useGroupEventBus` is a trailing debounce with no
 * max-wait, so while events keep arriving faster than its timeout the listener never fires at all. A
 * colleague editing one cell every few seconds could otherwise leave a tab rendering the first
 * `scheduledAt` it ever saw.
 */
export function usePropagationEvents(onState: (payload: EventInstrumentPropagationUpdate) => void): void {
	useEventBus("instrument-propagation-schedule-changed", onState);
	useEventBus("instrument-propagation-started", onState);
	useEventBus("instrument-propagation-done", onState);
}

/**
 * Subscribes to the completion event alone, for callers that react to an *outcome* rather than to the
 * state — the global failure notification.
 *
 * Use this and not {@link usePropagationEvents} for that: `lastRun` is part of the full state and
 * therefore rides along on every subsequent event too, so a failure inspected from a subscribe-all
 * handler would be re-announced on the next `started` or `schedule-changed`.
 */
export function usePropagationDone(onDone: (payload: EventInstrumentPropagationUpdate) => void): void {
	useEventBus("instrument-propagation-done", onDone);
}
